import { ChangeDetectionStrategy, Component, computed, Inject, OnInit, signal, Signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { CameraActions } from '@states/camera/camera.action-types';
import { PreloaderColor } from '@enums/shared.enum';
import { UiPreloaderComponent } from 'src/app/shared/ui-kit/ui-preloader/ui-preloader.component';
import { TestCamerasFlatRow, TestCamerasRowType, TestCamerasStatus } from 'src/app/cameras/camera.model';
import { TestCameraOneLevelArrayTableComponent } from './components/test-camera-one-level-array-table/test-camera-one-level-array-table.component';
import { UiButtonComponent } from '@ui';

export interface TestSelectedCameraModalData {
  cameraIds: string[],
  edgeIds: string[],
}

@Component({
  selector: 'app-test-selected-camera-modal',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatDialogClose,
    UiButtonComponent,
    TestCameraOneLevelArrayTableComponent,
    UiPreloaderComponent,
  ],
  templateUrl: './test-selected-camera-modal.component.html',
  styleUrl: './test-selected-camera-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestSelectedCameraModalComponent implements OnInit {
  public selectSelectedTestCamerasStatusSig: Signal<TestCamerasStatus[]> = this.store$.selectSignal(CameraSelectors.selectSelectedTestCamerasStatus);
  public selectIsLoadingSig: Signal<boolean> = this.store$.selectSignal(CameraSelectors.selectIsTestCamerasStatusLoading);

  public loaderColor = PreloaderColor;

  public flatCameraRowsSig: Signal<TestCamerasFlatRow[]> = computed(() => {
    const data = this.selectSelectedTestCamerasStatusSig();
    return data ? this.flattenCameraStatus(data) : [];
  });

  constructor(
    private store$: Store,
    private dialogRef: MatDialogRef<TestSelectedCameraModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TestSelectedCameraModalData,
  ) { }

  ngOnInit(): void {
    if (this.data?.cameraIds?.length && this.data?.edgeIds?.length) {
      this.store$.dispatch(
        CameraActions.GetTestCamerasStatus({
          cameraIds: this.data.cameraIds.join(','),
          edgeIds: this.data.edgeIds.join(','),
        }),
      );
    }
  }

  public close() {
    this.dialogRef.close();
  }

  private flattenCameraStatus(data: TestCamerasStatus[]): TestCamerasFlatRow[] {
    const result: TestCamerasFlatRow[] = [];

    data.forEach(edge => {
      result.push({
        type: TestCamerasRowType.Edge,
        edgeId: edge.edgeId,
        playbackStatus: edge.playbackStatus,
        eth0connected: edge.eth0connected,
        eth0speed: edge.eth0speed,
        eth1connected: edge.eth1connected,
        eth1speed: edge.eth1speed,
        totalLoad: edge.totalLoad
      });

      edge.cameras.forEach((cam, index) => {
        const isLast = index === edge.cameras.length - 1;

        result.push({
          type: TestCamerasRowType.Camera,
          cameraId: cam.cameraId,
          mainStream: cam.mainStream,
          substream: cam.substream,
          load: cam.load,
          ...(isLast ? {
            isLast: true,
          } : {})
        });
      });
    });

    return result;
  }
}
